import { SampleDto } from '@/api/ApiClient';

/**
 * Extract startingMaterials from a sample, handling both array and single object cases
 * @param sample - The sample to extract startingMaterials from (can be null or undefined)
 * @returns Array of startingMaterial samples, or undefined if none exist
 */
export const extractSampleStartingMaterials = (sample: SampleDto | null | undefined): SampleDto[] | undefined => {
  if (!sample?.sampleAsSubstrate) {
    return undefined;
  }

  // Type guard - API returns array but might be single object in some cases
  const startingMaterials = Array.isArray(sample.sampleAsSubstrate)
    ? sample.sampleAsSubstrate
    : [sample.sampleAsSubstrate];

  return startingMaterials.length > 0 ? startingMaterials : undefined;
};

/**
 * Formats an array of material names into a comma-separated string
 * @param materials - Array of material names
 * @returns Comma-separated string or 'N/A' if empty
 */
export const formatMaterials = (materials?: string[]): string => {
  return materials && materials.length > 0 ? materials.join(', ') : 'N/A';
};

/**
 * Formats substrate information from a sample
 * Combines substrateName and sampleAsSubstrate labels
 * @param sample - The sample to format substrate from
 * @returns Formatted substrate string or 'N/A'
 */
export const formatSubstrate = (sample?: SampleDto): string => {
  if (!sample) return 'N/A';
  const parts: string[] = [];
  if (sample.substrateName) {
    parts.push(sample.substrateName);
  }
  if (sample.sampleAsSubstrate && sample.sampleAsSubstrate.length > 0) {
    const labels = sample.sampleAsSubstrate.map((s) => s.sampleLabel).filter(Boolean);
    if (labels.length > 0) {
      parts.push(labels.join(', '));
    }
  }
  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

/**
 * Whether the sample has any values shown in the "Substrate Details" block (name, vendor, lot, orientation, purity, substrate material).
 * Used to hide the entire section in read-only mode when it would be an empty shell.
 */
export const sampleHasSubstrateDetailsData = (sample: SampleDto): boolean => {
  return Boolean(
    (sample.substrateName && sample.substrateName.trim() !== '') ||
    (sample.substrateVendor && sample.substrateVendor.trim() !== '') ||
    (sample.substrateLot && sample.substrateLot.trim() !== '') ||
    (sample.substrateOrientation && sample.substrateOrientation.trim() !== '') ||
    sample.substratePurity != null ||
    (sample.substrateMaterialIds?.length ?? 0) > 0
  );
};
